import { defineStore } from 'pinia'
import { ref, computed, onUnmounted } from 'vue'
import type { PackingList, Category, Item } from '@/types'
import { pocketbaseService, pb } from '@/services/pocketbase'

export const usePackingListStore = defineStore('packingList', () => {
  // State
  const currentPackingList = ref<PackingList | null>(null)
  const categories = ref<(Category & { items: Item[] })[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const subscriptions = ref<(() => void)[]>([])
  const movingItems = ref<Set<string>>(new Set()) // Track items being moved

  // Computed
  const totalItems = computed(() => {
    return categories.value.reduce((total, category) => {
      return total + category.items.length
    }, 0)
  })

  const completedItems = computed(() => {
    return categories.value.reduce((total, category) => {
      return total + category.items.filter(item => item.completed).length
    }, 0)
  })

  const progress = computed(() => {
    if (totalItems.value === 0) return 0
    return Math.round((completedItems.value / totalItems.value) * 100)
  })

  // Actions
  const loadPackingList = async (id: string) => {
    loading.value = true
    error.value = null
    
    try {
      const data = await pocketbaseService.getPackingListWithData(id)
      currentPackingList.value = data.packingList
      categories.value = data.categories
      // Start realtime subscriptions for this packing list
      await subscribeToRealtime(id)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load packing list'
      console.error('Failed to load packing list:', err)
    } finally {
      loading.value = false
    }
  }

  const createPackingList = async (title: string, description?: string) => {
    loading.value = true
    error.value = null
    
    try {
      const packingList = await pocketbaseService.createPackingList({
        title,
        description
      })
      currentPackingList.value = packingList
      categories.value = []
      // Start realtime subscriptions for the new packing list
      await subscribeToRealtime(packingList.id)
      return packingList.id
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create packing list'
      console.error('Failed to create packing list:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updatePackingList = async (data: Partial<PackingList>) => {
    if (!currentPackingList.value) return
    
    try {
      const updated = await pocketbaseService.updatePackingList(currentPackingList.value.id, data)
      currentPackingList.value = { ...currentPackingList.value, ...updated }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update packing list'
      console.error('Failed to update packing list:', err)
    }
  }

  const duplicatePackingList = async (sourceId: string, newTitle: string) => {
    loading.value = true
    error.value = null
    
    try {
      // Helper function to add delay between requests
      const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
      
      // Get the source list with all its data
      const sourceData = await pocketbaseService.getPackingListWithData(sourceId)
      await delay(100) // Small delay after fetching data
      
      // Create the new packing list
      const newPackingList = await pocketbaseService.createPackingList({
        title: newTitle,
        description: sourceData.packingList.description
      })
      await delay(100) // Delay after creating list
      
      // Duplicate all categories and their items sequentially
      for (let i = 0; i < sourceData.categories.length; i++) {
        const sourceCategory = sourceData.categories[i]
        
        // Create category
        const newCategory = await pocketbaseService.createCategory({
          name: sourceCategory.name,
          packing_list: newPackingList.id,
          order: sourceCategory.order
        })
        await delay(50) // Small delay after each category
        
        // Duplicate all items in this category sequentially
        for (let j = 0; j < sourceCategory.items.length; j++) {
          const sourceItem = sourceCategory.items[j]
          
          await pocketbaseService.createItem({
            name: sourceItem.name,
            category: newCategory.id,
            completed: false, // Reset completion status for new list
            quantity: sourceItem.quantity,
            notes: sourceItem.notes,
            order: sourceItem.order
          })
          
          // Add delay between items to prevent rate limiting
          if (j < sourceCategory.items.length - 1) {
            await delay(30)
          }
        }
        
        // Delay between categories
        if (i < sourceData.categories.length - 1) {
          await delay(100)
        }
      }
      
      return newPackingList.id
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to duplicate packing list'
      console.error('Failed to duplicate packing list:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const importFromYAML = async (yamlData: any) => {
    loading.value = true
    error.value = null
    
    try {
      // Helper function to add delay between requests
      const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
      
      // Create the new packing list
      const newPackingList = await pocketbaseService.createPackingList({
        title: yamlData.title,
        description: yamlData.description || ''
      })
      await delay(100)
      
      // Create categories and items from YAML data
      for (let i = 0; i < yamlData.categories.length; i++) {
        const categoryData = yamlData.categories[i]
        
        const newCategory = await pocketbaseService.createCategory({
          name: categoryData.name,
          packing_list: newPackingList.id,
          order: i + 1
        })
        await delay(50)
        
        // Create items for this category
        if (categoryData.items && Array.isArray(categoryData.items)) {
          for (let j = 0; j < categoryData.items.length; j++) {
            const itemData = categoryData.items[j]
            
            await pocketbaseService.createItem({
              name: itemData.name,
              category: newCategory.id,
              completed: itemData.completed || false,
              quantity: itemData.quantity || 1,
              notes: itemData.notes || '',
              order: j + 1
            })
            
            if (j < categoryData.items.length - 1) {
              await delay(30)
            }
          }
        }
        
        if (i < yamlData.categories.length - 1) {
          await delay(100)
        }
      }
      
      return newPackingList.id
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to import from YAML'
      console.error('Failed to import from YAML:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createCategory = async (name: string) => {
    if (!currentPackingList.value) return
    
    try {
      const order = categories.value.length + 1  // Start from 1, not 0
      const category = await pocketbaseService.createCategory({
        name,
        packing_list: currentPackingList.value.id,
        order
      })
      // Don't add to local state here - let realtime subscription handle it
      // This prevents duplicates
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create category'
      console.error('Failed to create category:', err)
    }
  }

  const updateCategory = async (id: string, data: Partial<Category>) => {
    try {
      const updated = await pocketbaseService.updateCategory(id, data)
      const index = categories.value.findIndex(c => c.id === id)
      if (index !== -1) {
        categories.value[index] = { ...categories.value[index], ...updated }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update category'
      console.error('Failed to update category:', err)
    }
  }

  const deleteCategory = async (id: string) => {
    try {
      await pocketbaseService.deleteCategory(id)
      // Don't update local state here - let realtime subscription handle it
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete category'
      console.error('Failed to delete category:', err)
    }
  }

  const createItem = async (categoryId: string, name: string, quantity?: number) => {
    try {
      const category = categories.value.find(c => c.id === categoryId)
      if (!category) return
      
      const order = category.items.length + 1  // Start from 1, not 0
      const item = await pocketbaseService.createItem({
        name,
        category: categoryId,
        completed: false,
        quantity,
        order
      })
      // Don't add to local state here - let realtime subscription handle it
      // This prevents duplicates
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create item'
      console.error('Failed to create item:', err)
    }
  }

  const updateItem = async (id: string, data: Partial<Item>) => {
    try {
      const updated = await pocketbaseService.updateItem(id, data)
      for (const category of categories.value) {
        const index = category.items.findIndex(i => i.id === id)
        if (index !== -1) {
          category.items[index] = { ...category.items[index], ...updated }
          break
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update item'
      console.error('Failed to update item:', err)
    }
  }

  const deleteItem = async (id: string) => {
    try {
      await pocketbaseService.deleteItem(id)
      // Don't update local state here - let realtime subscription handle it
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete item'
      console.error('Failed to delete item:', err)
    }
  }

  const reorderCategories = async (newOrder: string[]) => {
    try {
      console.log('Store: reorderCategories called with:', newOrder)
      
      // Update local state immediately for responsive UI
      // Update the order property of each category in place
      newOrder.forEach((id, index) => {
        const category = categories.value.find(c => c.id === id)
        if (category) {
          category.order = index + 1
        } else {
          console.error('Category not found:', id)
        }
      })

      // Sort categories by the new order
      categories.value.sort((a, b) => a.order - b.order)
      console.log('Updated categories order:', categories.value.map(c => ({ id: c.id, name: c.name, order: c.order })))

      // Update backend with delays to prevent rate limiting
      for (let i = 0; i < newOrder.length; i++) {
        const categoryId = newOrder[i]
        console.log(`Updating category ${categoryId} to order ${i + 1}`)
        await pocketbaseService.updateCategory(categoryId, { order: i + 1 })
        if (i < newOrder.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
      }
      console.log('Category reorder completed successfully')
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to reorder categories'
      console.error('Failed to reorder categories:', err)
      // Reload data on error to ensure consistency
      if (currentPackingList.value) {
        await loadPackingList(currentPackingList.value.id)
      }
    }
  }

  const reorderItems = async (categoryId: string, newOrder: string[]) => {
    console.log('Store: reorderItems called with:', categoryId, newOrder)
    try {
      const category = categories.value.find(c => c.id === categoryId)
      if (!category) {
        console.error('Category not found:', categoryId)
        return
      }

      console.log('Found category:', category.name, 'with items:', category.items.map(i => ({ id: i.id, name: i.name, order: i.order })))

      // Update local state immediately for responsive UI
      // Instead of replacing the array, update the order property of each item in place
      newOrder.forEach((id, index) => {
        const item = category.items.find(i => i.id === id)
        if (item) {
          item.order = index + 1
        } else {
          console.error('Item not found in category:', id)
        }
      })

      // Sort the items by the new order
      category.items.sort((a, b) => a.order - b.order)
      console.log('Updated items order:', category.items.map(i => ({ id: i.id, name: i.name, order: i.order })))

      // Update backend with delays to prevent rate limiting
      for (let i = 0; i < newOrder.length; i++) {
        const itemId = newOrder[i]
        console.log(`Updating item ${itemId} to order ${i + 1}`)
        await pocketbaseService.updateItem(itemId, { order: i + 1 })
        if (i < newOrder.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 30))
        }
      }
      console.log('Reorder completed successfully')
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to reorder items'
      console.error('Failed to reorder items:', err)
      // Reload data on error to ensure consistency
      if (currentPackingList.value) {
        await loadPackingList(currentPackingList.value.id)
      }
    }
  }

  const moveItemToCategory = async (itemId: string, newCategoryId: string, newOrder: number) => {
    try {
      // Mark item as being moved to prevent realtime conflicts
      movingItems.value.add(itemId)

      // Find the item and its current category
      let sourceCategory: (Category & { items: Item[] }) | undefined
      let itemIndex = -1
      
      for (const category of categories.value) {
        const index = category.items.findIndex(i => i.id === itemId)
        if (index !== -1) {
          sourceCategory = category
          itemIndex = index
          break
        }
      }
      
      if (!sourceCategory || itemIndex === -1) {
        movingItems.value.delete(itemId)
        return
      }

      const item = sourceCategory.items[itemIndex]
      const targetCategory = categories.value.find(c => c.id === newCategoryId)
      if (!targetCategory) {
        movingItems.value.delete(itemId)
        return
      }

      // Update local state immediately for responsive UI
      sourceCategory.items.splice(itemIndex, 1)
      
      // Update the item's category and order
      const updatedItem = { ...item, category: newCategoryId, order: newOrder + 1 }
      targetCategory.items.splice(newOrder, 0, updatedItem)

      // Fix order numbers in target category
      targetCategory.items.forEach((item, index) => {
        item.order = index + 1
      })
      
      // Sort target category by order
      targetCategory.items.sort((a, b) => a.order - b.order)

      // Update backend
      await pocketbaseService.updateItem(itemId, { 
        category: newCategoryId, 
        order: newOrder + 1 
      })

      // Update order for other items in target category
      for (let i = 0; i < targetCategory.items.length; i++) {
        const currentItem = targetCategory.items[i]
        if (currentItem.id !== itemId && currentItem.order !== i + 1) {
          await pocketbaseService.updateItem(currentItem.id, { order: i + 1 })
          await new Promise(resolve => setTimeout(resolve, 30))
        }
      }

      // Remove from moving items after a delay to allow backend update to propagate
      setTimeout(() => {
        movingItems.value.delete(itemId)
      }, 1000)

    } catch (err) {
      movingItems.value.delete(itemId)
      error.value = err instanceof Error ? err.message : 'Failed to move item'
      console.error('Failed to move item:', err)
      // Reload data on error to ensure consistency
      if (currentPackingList.value) {
        await loadPackingList(currentPackingList.value.id)
      }
    }
  }

  // Realtime subscriptions
  const subscribeToRealtime = async (packingListId: string) => {
    unsubscribeFromRealtime()
    
    try {
      // Subscribe to packing list updates
      const unsubPackingList = await pb.collection('packing_lists').subscribe(packingListId, (e) => {
        if (e.action === 'update' && currentPackingList.value) {
          currentPackingList.value = { ...currentPackingList.value, ...e.record }
        }
      })

      // Subscribe to categories
      const unsubCategories = await pb.collection('categories').subscribe('*', (e) => {
        if (e.record.packing_list !== packingListId) return

        switch (e.action) {
          case 'create':
            const existingCategory = categories.value.find(c => c.id === e.record.id)
            if (!existingCategory) {
              categories.value.push({ ...e.record, items: [] })
              categories.value.sort((a, b) => a.order - b.order)
            }
            break
          case 'update':
            const categoryIndex = categories.value.findIndex(c => c.id === e.record.id)
            if (categoryIndex !== -1) {
              categories.value[categoryIndex] = { 
                ...categories.value[categoryIndex], 
                ...e.record 
              }
              categories.value.sort((a, b) => a.order - b.order)
            }
            break
          case 'delete':
            const deleteIndex = categories.value.findIndex(c => c.id === e.record.id)
            if (deleteIndex !== -1) {
              categories.value.splice(deleteIndex, 1)
            }
            break
        }
      })

      // Subscribe to items
      const unsubItems = await pb.collection('items').subscribe('*', (e) => {
        // Skip updates for items currently being moved by this client
        if (movingItems.value.has(e.record.id)) {
          return
        }

        const category = categories.value.find(c => c.id === e.record.category)
        
        switch (e.action) {
          case 'create':
            if (!category) return
            const existingItem = category.items.find(i => i.id === e.record.id)
            if (!existingItem) {
              category.items.push(e.record)
              category.items.sort((a, b) => a.order - b.order)
            }
            break
          case 'update':
            // Handle item moved to different category (from other clients)
            const oldCategory = categories.value.find(c => c.items.some(i => i.id === e.record.id))
            const newCategory = categories.value.find(c => c.id === e.record.category)
            
            if (oldCategory && newCategory && oldCategory.id !== newCategory.id) {
              // Remove from old category
              const oldIndex = oldCategory.items.findIndex(i => i.id === e.record.id)
              if (oldIndex !== -1) {
                oldCategory.items.splice(oldIndex, 1)
              }
              // Add to new category
              const existingInNew = newCategory.items.find(i => i.id === e.record.id)
              if (!existingInNew) {
                newCategory.items.push(e.record)
                newCategory.items.sort((a, b) => a.order - b.order)
              }
            } else if (newCategory) {
              // Update in same category
              const itemIndex = newCategory.items.findIndex(i => i.id === e.record.id)
              if (itemIndex !== -1) {
                newCategory.items[itemIndex] = { ...newCategory.items[itemIndex], ...e.record }
                newCategory.items.sort((a, b) => a.order - b.order)
              }
            }
            break
          case 'delete':
            // Find and remove from any category
            for (const cat of categories.value) {
              const deleteItemIndex = cat.items.findIndex(i => i.id === e.record.id)
              if (deleteItemIndex !== -1) {
                cat.items.splice(deleteItemIndex, 1)
                break
              }
            }
            break
        }
      })

      subscriptions.value = [unsubPackingList, unsubCategories, unsubItems]
    } catch (err) {
      console.error('Failed to set up realtime subscriptions:', err)
      // Fallback: continue without realtime if it fails
    }
  }

  const unsubscribeFromRealtime = () => {
    subscriptions.value.forEach(unsub => unsub())
    subscriptions.value = []
    movingItems.value.clear() // Clear moving items when unsubscribing
  }

  // Cleanup on store destruction
  onUnmounted(() => {
    unsubscribeFromRealtime()
  })

  return {
    // State
    currentPackingList,
    categories,
    loading,
    error,
    // Computed
    totalItems,
    completedItems,
    progress,
    // Actions
    loadPackingList,
    createPackingList,
    updatePackingList,
    duplicatePackingList,
    importFromYAML,
    createCategory,
    updateCategory,
    deleteCategory,
    createItem,
    updateItem,
    deleteItem,
    reorderCategories,
    reorderItems,
    moveItemToCategory,
    subscribeToRealtime,
    unsubscribeFromRealtime
  }
})